'use client';

import { c as _c } from "react/compiler-runtime";
import { keysOf } from 'betterplace-utils';
import classNames from 'classnames';
import { Button, Heading, LayoutStack } from 'design-system/client';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect } from 'react';
import { useStep } from '../IFrameLayout/StepProvider';
import IFrameAddressFields from './IFrameAddressFields';
import IFrameExplanation from './IFrameExplanation';
import styles from './IFrameForm.module.css';
import IFrameHolderFields from './IFrameHolderFields';
import IFrameIntervalSelection from './IFrameIntervalSelection';
import PaymentMethodSelection from './PaymentMethodSelection';
import ProhibitedNotice from './ProhibitedNotice';
import TaxDeductibilityNotice from './TaxDeductibilityNotice';
import { StepProps } from './types';
import { Messages, useMessageContext } from '@/components/Messages';
import type { DonationFormProps } from '@/donationForm/types';
import AddressFieldToggle from '@/donationPages/_dependencies/components/formParts/AddressFieldToggle/AddressFieldToggle';
import CompanyFields from '@/donationPages/_dependencies/components/formParts/CompanyFields';
import Debug from '@/donationPages/_dependencies/components/formParts/Debug';
import DonationFormCaptcha from '@/donationPages/_dependencies/components/formParts/DonationFormCaptcha';
import GeneralError from '@/donationPages/_dependencies/components/formParts/GeneralError';
import HolderHeadline from '@/donationPages/_dependencies/components/formParts/HolderHeadline';
import HolderTitle from '@/donationPages/_dependencies/components/formParts/HolderTitle';
import PaymentMethodError from '@/donationPages/_dependencies/components/formParts/PaymentMethodError';
import StripeCCFields from '@/donationPages/_dependencies/components/formParts/StripeFields/StripeCCFields';
import StripeSEPADebitFields from '@/donationPages/_dependencies/components/formParts/StripeFields/StripeSEPADebitFields';
import SubmitButton from '@/donationPages/_dependencies/components/formParts/SubmitButton';
import Terms from '@/donationPages/_dependencies/components/formParts/Terms';
import { SSLLogo } from '@/donationPages/_dependencies/components/formParts/TrustLogos';
import { useDonationFormContext, useDonationFormValues } from '@/donationPages/_dependencies/helpers';
import DonationAmountField from '@/donationPages/iframe/[receiver-type]/[slug]/_dependencies/IFrameForm/DonationAmountField';
import Summary from '@/donationPages/iframe/[receiver-type]/[slug]/_dependencies/IFrameForm/Summary';
import { report } from '@/errorReporting';
import { postMessageScrollTop } from '@/helpers/utils';
import { useShowPaymentError } from '@/helpers/hooks';
function postMessageFormLoading() {
  if (window.parent) window.parent.postMessage('bpIframeLoadStart', '*');
}
function IFrameBlockWrapper(t0) {
  const $ = _c(5);
  const {
    headingText,
    children
  } = t0;
  let t1;
  if ($[0] !== headingText) {
    t1 = <Heading level="heading-xs" className={styles.headingMargin}>{headingText}</Heading>;
    $[0] = headingText;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={styles.blockWrapper}>{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function Step1({
  visible
}: StepProps) {
  const t = useTranslations('nextjs');
  const [companyDonation] = useDonationFormValues(['_company_donation']);
  const {
    addMessage
  } = useMessageContext();
  const {
    setStep
  } = useStep();
  const {
    trigger,
    setValue,
    getValues,
    formState: {
      errors
    }
  } = useDonationFormContext();
  const validateAndGoToNextStep = useCallback(async () => {
    try {
      const valid = await trigger(undefined, {
        shouldFocus: true
      });
      postMessageScrollTop();
      if (valid) {
        setStep(2);
        return;
      }
      // set all fields with errors to touched
      for (const key of keysOf(errors)) {
        if (key === 'root') continue;
        if (errors[key]) {
          const value = getValues(key);
          setValue(key, value, {
            shouldTouch: true
          });
        }
      }
    } catch (error) {
      report(error);
      addMessage({
        type: 'error',
        message: t('errors.messages.validate')
      });
    }
  }, [trigger, setStep, errors, getValues, setValue, addMessage, t]);
  const onContinueClicked = useCallback(() => {
    void validateAndGoToNextStep();
  }, [validateAndGoToNextStep]);
  return <div className="donate-iframe-content" style={{
    display: visible ? 'block' : 'none'
  }}>
      <div className={classNames('max-w-screen-xs', styles.iFrameInnerContainer)}>
        <Messages />
        <GeneralError />
        <IFrameBlockWrapper headingText={t('donate.iframe.recurring_headline')}>
          <IFrameIntervalSelection />
        </IFrameBlockWrapper>
        <IFrameBlockWrapper headingText={t('donate.iframe.amount_headline')}>
          <DonationAmountField className={styles.donationAmountInput} invisibleLabel />
        </IFrameBlockWrapper>
        <PaymentMethodSelection layout="horizontal" className={styles.paymentMethodSelection} />
        <StripeCCFields />
        <StripeSEPADebitFields />
        <PaymentMethodError />

        <LayoutStack space="400" className={styles.blockWrapper}>
          <HolderHeadline />
          <IFrameHolderFields />

          {companyDonation && <>
              <HolderTitle />
              <CompanyFields />
              <IFrameAddressFields required />
            </>}
          {!companyDonation && <div className={styles.blockWrapper}>
              <AddressFieldToggle>
                <IFrameAddressFields required />
              </AddressFieldToggle>
            </div>}
        </LayoutStack>

        <div className={styles.continueContainer}>
          <Button onClick={onContinueClicked} type="button" kind="primary" className="iframeButton">
            {t('donate.iframe.continue_button')}
          </Button>
          <SSLLogo className={styles.sslLogo} />
        </div>
        <DonationFormCaptcha />
      </div>
    </div>;
}
function Step2(t0) {
  const $ = _c(7);
  const {
    visible
  } = t0;
  const t1 = visible ? "block" : "none";
  let t2;
  if ($[0] !== t1) {
    t2 = {
      display: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = classNames("max-w-screen-xs", styles.iFrameInnerContainer);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Summary explanation={<IFrameExplanation />} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className={t3}>{t4}<div className={styles.continueContainer}><SubmitButton className="iframeButton" /><SSLLogo className={styles.sslLogo} /></div><TaxDeductibilityNotice /><br /><Terms /></div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t2) {
    t6 = <div className="donate-iframe-content" style={t2}>{t5}</div>;
    $[5] = t2;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}
function IFrameForm(t0) {
  const $ = _c(23);
  let initialFFs;
  let initialFormValues;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      config: t1,
      initialFormValues,
      initialFFs,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = initialFFs;
    $[2] = initialFormValues;
    $[3] = props;
    $[4] = t1;
  } else {
    initialFFs = $[1];
    initialFormValues = $[2];
    props = $[3];
    t1 = $[4];
  }
  const {
    donationsProhibited
  } = t1;
  const {
    step
  } = useStep();
  useShowPaymentError();
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useEffect(_temp, t2);
  if (donationsProhibited) {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <ProhibitedNotice />;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  const t3 = step === 1;
  let t4;
  if ($[7] !== t3) {
    t4 = <Step1 visible={t3} />;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = step === 2;
  let t6;
  if ($[9] !== t5) {
    t6 = <Step2 visible={t5} />;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <div>{t4}{t6}</div>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== props || $[15] !== t7) {
    t8 = <form {...props} className="in-iframe">{t7}</form>;
    $[14] = props;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== initialFFs || $[18] !== initialFormValues) {
    t9 = <Debug initialFFs={initialFFs} initialFormValues={initialFormValues} />;
    $[17] = initialFFs;
    $[18] = initialFormValues;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function _temp() {
  postMessageFormLoading();
}
export default IFrameForm;